<template>
  <!-- standard section -->
  <div v-if="section.type === 'standard'" class="form-section">
    <div v-if="section.subtitle" class="text-caption text-grey mb-2">{{ tl(section.subtitle) }}</div>

    <!-- Regular fields -->
    <div class="field-grid" :style="section.columns ? { gridTemplateColumns: `repeat(${section.columns}, 1fr)` } : undefined">
      <template v-for="field in section.fields" :key="field.id">
        <!-- Divider (label + line) -->
        <!--
          groupTitle — CRS 처럼 소제목으로 묶어 보여 준다("Statement from EPF").
          제목만 뽑아 앞에 깔고, 필드는 그대로 이어서 그린다.
        -->
        <div
          v-if="field.groupTitle && isFieldVisible(field)"
          class="field-col-full pt-3 pb-1"
        >
          <div class="text-subtitle-2">{{ tl(field.groupTitle) }}</div>
          <v-divider class="mt-1" />
        </div>
        <div v-if="field.type === 'divider' && isFieldVisible(field)" class="field-col-full pt-2 pb-1">
          <div class="text-caption text-medium-emphasis">{{ tl(field.label) }}</div>
          <v-divider />
        </div>
        <!-- Regular field -->
        <FieldRenderer
          v-else-if="isFieldVisible(field)"
          :field="field"
          :model-value="getFieldValue(field.id)"
          :readonly="readonly"
          :ref-data="refData"
          :density="density"
          :show-errors="showErrors"
          :entities="entities"
          :entities-loading="entitiesLoading"
          :entity-search-value="entitySearchValue"
          :contacts="contacts"
          :contacts-loading="contactsLoading"
          :error-message="fieldError(`${section.id}.${field.id}`)"
          :field-key="`${section.id}.${field.id}`"
          @update:model-value="setFieldValue(field.id, $event)"
          @date-picker-change="handleStandardDatePick(field.id, $event)"
          @date-blur="handleStandardDateBlur(field.id)"
          @file-change="handleFileChange"
          @entity-change="emit('entity-change', $event)"
          @contact-change="(val) => emit('contact-change', { sectionId: section.id, fieldId: field.id, value: val })"
          @field-action="forwardFieldAction"
        />
      </template>
    </div>

    <!-- Address Blocks -->
    <AddressBlock
      v-for="addrBlock in section.addressBlocks"
      :key="addrBlock.id"
      :address-id="addrBlock.id"
      :title="addrBlock.title"
      :model-value="getAddressValue(addrBlock.id)"
      :template-ref="addrBlock.templateRef"
      :include-optional="addrBlock.includeOptional"
      :default-country="addrBlock.defaultCountry"
      :same-as="addrBlock.sameAs ? { ...addrBlock.sameAs } : null"
      :source-address="addrBlock.sameAs ? getAddressValue(addrBlock.sameAs.target) : {}"
      :available-toggle="addrBlock.availableToggle ? { ...addrBlock.availableToggle } : null"
      :readonly="readonly"
      :ref-data="refData"
      :density="density"
      :resolve-template-fn="resolveTemplateFn"
      :show-errors="showErrors"
      :error-prefix="`${section.id}._addr.${addrBlock.id}.`"
      :field-errors="fieldErrors"
      :lock-country="addrBlock.lockCountry || false"
      @update:model-value="setAddressValue(addrBlock.id, $event)"
    />

    <!-- Conditional Groups (e.g., employees section) -->
    <template v-for="cg in section.conditionalGroups" :key="cg.id">
      <div v-if="isConditionalGroupVisible(cg)" class="mt-4">
        <div class="field-grid">
          <template v-for="field in cg.fields" :key="field.id">
            <!-- section.fields 와 동일하게 groupTitle 을 소제목으로 깔아 준다(CRS 화면과 같은 묶음). -->
            <div v-if="field.groupTitle" class="field-col-full pt-3 pb-1">
              <div class="text-subtitle-2">{{ tl(field.groupTitle) }}</div>
              <v-divider class="mt-1" />
            </div>
            <FieldRenderer
              :field="field"
              :model-value="getFieldValue(field.id)"
              :readonly="readonly || field.readonly"
              :ref-data="refData"
              :density="density"
              :error-message="fieldError(`${section.id}.${field.id}`)"
              :field-key="`${section.id}.${field.id}`"
              @update:model-value="handleConditionalFieldUpdate(field, $event)"
            />
          </template>
        </div>
      </div>
    </template>

    <!-- Extra Groups (e.g., contact info) -->
    <template v-for="eg in section.extraGroups" :key="eg.id">
      <div class="mt-4">
        <div class="field-grid">
          <template v-for="field in eg.fields" :key="field.id">
            <!-- section.fields / conditionalGroups 와 같게 groupTitle 을 소제목으로 깔아 준다.
                 여기만 빠져 있어서 "Contact (Lettaga record only ...)" 안내가 화면에 없었다. -->
            <div v-if="field.groupTitle" class="field-col-full pt-3 pb-1">
              <div class="text-subtitle-2">{{ tl(field.groupTitle) }}</div>
              <v-divider class="mt-1" />
            </div>
            <FieldRenderer
              :field="field"
              :model-value="getFieldValue(field.id)"
              :readonly="readonly"
              :ref-data="refData"
              :density="density"
              :error-message="fieldError(`${section.id}.${field.id}`)"
              :field-key="`${section.id}.${field.id}`"
              @update:model-value="setFieldValue(field.id, $event)"
            />
          </template>
        </div>
      </div>
    </template>

    <!-- Branch Addresses -->
    <template v-if="section.branchAddress?.enabled">
      <div class="mt-4">
        <AddressBlock
          v-for="(addr, bIdx) in branchAddresses"
          :key="'branch-' + bIdx"
          :address-id="'branch-' + bIdx"
          :title="tl('Branch Address') + ' #' + (bIdx + 1)"
          :model-value="addr"
          :template-ref="section.branchAddress!.templateRef"
          :include-optional="section.branchAddress!.includeOptional"
          :readonly="readonly"
          :ref-data="refData"
          :density="density"
          :resolve-template-fn="resolveTemplateFn"
          @update:model-value="updateBranchAddress(bIdx, $event)"
        />
        <div class="d-flex gap-2 mt-2 align-center">
          <!-- CRS 는 지점주소를 1건만 받는다. 더 추가해도 조용히 버려지므로 버튼을 막는다. -->
          <v-btn
            v-if="!readonly && branchAddresses.length < (section.branchAddress!.maxItems || Infinity)"
            variant="tonal"
            color="primary"
            size="small"
            @click="addBranchAddress"
          >
            {{ tl(section.branchAddress!.addLabel) }}
          </v-btn>
          <span
            v-else-if="!readonly && section.branchAddress!.maxItemsHint"
            class="text-caption text-medium-emphasis"
          >
            {{ tl(section.branchAddress!.maxItemsHint!) }}
          </span>
          <v-btn
            v-if="!readonly && branchAddresses.length > 0"
            variant="tonal"
            color="error"
            size="small"
            @click="removeBranchAddress"
          >
            {{ tl('Remove Last') }}
          </v-btn>
        </div>
      </div>
    </template>
  </div>

  <!-- attachment-list section (Supporting Documents) — CRS 와 동일하게 표 + Add Attachment -->
  <div v-else-if="section.type === 'attachment-list'" class="form-section">
    <div v-if="section.info" class="text-caption text-grey mb-3">{{ tl(section.info) }}</div>

    <v-table density="compact" class="mb-2">
      <thead>
        <tr>
          <th style="width:64px">{{ tl('No.') }}</th>
          <th>{{ tl('Document Description') }}</th>
          <th style="width:320px">{{ tl('Document') }}</th>
          <th style="width:56px"></th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(row, idx) in attachmentRows" :key="'att-' + idx">
          <td>{{ idx + 1 }}</td>
          <td>
            <v-text-field
              :model-value="row.description"
              :placeholder="tl('e.g. Letter of consent')"
              :readonly="readonly"
              :maxlength="200"
              variant="outlined"
              density="compact"
              hide-details
              @update:model-value="updateAttachment(idx, 'description', $event)"
            />
          </td>
          <td>
            <!-- 임시저장으로 CRS 에 이미 올라간 첨부는 파일을 다시 고를 필요가 없다 —
                 파일명을 보여 주고, 바꾸고 싶을 때만 새로 고르게 한다. -->
            <div v-if="!row.file && row.documentId && row.fileName" class="d-flex align-center ga-1">
              <v-icon size="16" color="success">mdi-paperclip-check</v-icon>
              <span class="text-caption text-truncate" :title="row.fileName">{{ row.fileName }}</span>
              <v-btn
                v-if="!readonly"
                variant="text"
                size="x-small"
                @click="updateAttachment(idx, 'file', null)"
              >
                {{ tl('Replace') }}
              </v-btn>
            </div>
            <v-file-input
              v-else
              :model-value="row.file"
              :accept="section.accept || '.pdf,.jpg,.jpeg,.png'"
              :readonly="readonly"
              :label="row.file ? '' : tl('Attach file')"
              variant="outlined"
              density="compact"
              hide-details
              prepend-icon=""
              prepend-inner-icon="mdi-paperclip"
              @update:model-value="updateAttachment(idx, 'file', $event)"
            />
          </td>
          <td>
            <v-btn v-if="!readonly" icon variant="text" size="small" color="error" @click="removeAttachment(idx)">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </td>
        </tr>
        <tr v-if="!attachmentRows.length">
          <td colspan="4" class="text-center text-grey py-4">{{ tl('No records') }}</td>
        </tr>
      </tbody>
    </v-table>

    <v-btn
      v-if="!readonly && attachmentRows.length < (section.maxItems || 10)"
      variant="tonal"
      color="primary"
      size="small"
      @click="addAttachment"
    >
      <v-icon start size="16">mdi-plus-circle-outline</v-icon>
      {{ tl(section.addLabel || '+ Add Attachment') }}
    </v-btn>
  </div>

  <!-- simple-list section (Business Codes) -->
  <div v-else-if="section.type === 'simple-list'" class="form-section">
    <div v-if="section.info" class="text-caption text-grey mb-3">{{ tl(section.info) }}</div>

    <div v-for="(item, idx) in listItems" :key="idx" class="d-flex align-center gap-2 mb-2">
      <div class="flex-grow-1">
        <FieldRenderer
          :field="{ ...(section.fieldConfig as any), id: 'item-' + idx }"
          :model-value="item"
          :readonly="readonly"
          :ref-data="refData"
          :density="density"
          :error-message="fieldError(`${section.id}[${idx}]`)"
          :field-key="`${section.id}[${idx}]`"
          @update:model-value="updateListItem(idx, $event)"
        />
      </div>
      <v-btn
        v-if="!readonly && listItems.length > (section.minItems || 1)"
        icon
        variant="text"
        size="small"
        color="error"
        @click="removeListItem(idx)"
      >
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </div>

    <!-- maxItems 도달 시 추가 버튼을 감춘다(MSIC 는 CRS 가 최대 3개) -->
    <v-btn
      v-if="!readonly && listItems.length < (section.maxItems || Infinity)"
      variant="tonal"
      color="primary"
      size="small"
      class="mt-2"
      @click="addListItem"
    >
      {{ tl(section.addLabel || '+ Add') }}
    </v-btn>
    <div v-else-if="!readonly" class="text-caption text-grey mt-2">
      {{ tl('Maximum') }} {{ section.maxItems }} {{ tl('items') }}
    </div>
  </div>

  <!-- dynamic-list section (Directors, Shareholders, Promoters) -->
  <div v-else-if="section.type === 'dynamic-list'" class="form-section">
    <div v-if="section.info" class="text-caption text-grey mb-3">{{ tl(section.info) }}</div>

    <DynamicListItem
      v-for="(item, idx) in dynamicItems"
      :key="idx"
      :index="idx"
      :section-id="section.id"
      :item-data="item"
      :item-label="tl(section.itemLabel || 'Item')"
      :border-color="section.borderColor"
      :theme-color="section.themeColor"
      :readonly="readonly"
      :ref-data="refData"
      :density="density"
      :header-fields="section.itemTemplate?.headerFields || []"
      :type-selector="section.itemTemplate?.typeSelector || null"
      :variants="section.itemTemplate?.variants || null"
      :groups="section.itemTemplate?.groups || []"
      :common-groups="section.itemTemplate?.commonGroups || []"
      :resolve-template-fn="resolveTemplateFn"
      :resolve-group-fn="resolveGroupFn"
      :show-errors="showErrors"
      :field-errors="fieldErrors"
      :entities="entities"
      :entities-loading="entitiesLoading"
      :contacts="contacts"
      :contacts-loading="contactsLoading"
      :contact-placeholder="contactPlaceholder"
      :contact-hint="contactHint"
      @update:item="updateDynamicItem(idx, $event)"
      @remove="removeDynamicItem(idx)"
      @member-toggle="handleMemberToggle(idx, $event)"
      @entity-change="emit('entity-change', $event)"
      @contact-change="(val) => emit('contact-change', { sectionId: section.id, index: idx, value: val })"
      @date-picker-change="(val) => emit('date-picker-change', { sectionId: section.id, index: idx, value: val })"
      @date-blur="emit('date-blur', { sectionId: section.id, index: idx })"
    />

    <v-btn
      v-if="!readonly"
      variant="tonal"
      color="primary"
      size="small"
      class="mt-2"
      @click="addDynamicItem"
    >
      {{ tl(section.addLabel || '+ Add') }}
    </v-btn>
  </div>

  <!-- declaration section -->
  <div v-else-if="section.type === 'declaration'" class="form-section">
    <v-alert type="info" variant="tonal" class="mb-3">
      {{ tl(section.text ?? '') }}
    </v-alert>
    <!--
      CRS 는 draft 저장 시 이 선언을 자기 화면에 되바인딩하지 않는다(실측).
      여기서 체크했어도 CRS 에서 한 번 더 체크해야 제출되므로 그 사실을 알려 준다.
    -->
    <p v-if="section.note" class="text-caption text-medium-emphasis mb-2">
      {{ tl(section.note) }}
    </p>
    <div :data-field-key="`${section.id}.declaration`">
      <v-checkbox
        :model-value="getFieldValue('declaration')"
        :label="tl('I agree to the above declaration')"
        :required="section.required"
        :readonly="readonly"
        color="primary"
        :error="!!fieldError(`${section.id}.declaration`)"
        :error-messages="fieldError(`${section.id}.declaration`)"
        @update:model-value="setFieldValue('declaration', $event)"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useTranslation } from '@/utils/locales/messages';
import { useLanguageStore } from '@/shared/stores/languageStore';
import { useSessionStore } from '@/shared/stores/sessionStore';
import { formatDateOnly } from '@/shared/composables/dateUtils';
import FieldRenderer from './FieldRenderer.vue';
import AddressBlock from './AddressBlock.vue';
import DynamicListItem from './DynamicListItem.vue';
import type { SectionDef, FieldDef, TemplateGroup, RefDataItem } from '../composables/useFormRenderer';

// Translation helper
const languageStore = useLanguageStore();
const { createSyncT } = useTranslation();
const t = createSyncT(languageStore.currentLanguage, { silent: true });
const tl = (text: string) => {
  if (!text) return '';
  const translated = t(`startTask.${text}`);
  return (translated && translated !== `startTask.${text}`) ? translated : text;
};

interface Props {
  section: SectionDef;
  formData: Record<string, any>;
  readonly?: boolean;
  refData?: Record<string, RefDataItem[]>;
  density?: 'compact' | 'comfortable';
  // Template resolve functions (from form-specific composable)
  resolveTemplateFn?: (ref: string, includeOptional?: boolean) => FieldDef[];
  resolveGroupFn?: (group: TemplateGroup) => FieldDef[];
  // Show address validation errors
  showErrors?: boolean;
  fieldErrors?: Record<string, string>;
  // RN-specific props
  entities?: any[];
  entitiesLoading?: boolean;
  entitySearchValue?: string | null;
  contacts?: any[];
  contactsLoading?: boolean;
  contactPlaceholder?: string;
  contactHint?: string;
}

const props = withDefaults(defineProps<Props>(), {
  readonly: false,
  refData: () => ({}),
  density: 'compact',
  resolveTemplateFn: undefined,
  resolveGroupFn: undefined,
  showErrors: false,
  fieldErrors: () => ({}),
  entities: () => [],
  entitiesLoading: false,
  entitySearchValue: null,
  contacts: () => [],
  contactsLoading: false,
  contactPlaceholder: '',
  contactHint: '',
});

const emit = defineEmits<{
  'update:field': [payload: { sectionId: string; fieldId: string; value: any }];
  'update:address': [payload: { sectionId: string; addressId: string; value: Record<string, any> }];
  'update:list': [payload: { sectionId: string; items: any[] }];
  'update:attachments': [payload: { sectionId: string; items: any[] }];
  'update:dynamicList': [payload: { sectionId: string; items: any[] }];
  'member-toggle': [payload: { index: number; isAlsoMember: boolean; directorData: Record<string, any> }];
  'entity-change': [entityCd: string | null];
  /** index 는 동적 목록(이사·주주), fieldId 는 일반 섹션(비서) 에서 온다. */
  'contact-change': [payload: { sectionId: string; index?: number; fieldId?: string; value: any }];
  'date-picker-change': [payload: { sectionId: string; index: number; value: any }];
  'date-blur': [payload: { sectionId: string; index: number }];
  'field-action': [payload: { sectionId: string; action: string; fieldId: string }];
}>();

// Simple field get/set
function fieldError(key: string): string | undefined {
  if (!props.showErrors) return undefined;
  return props.fieldErrors?.[key];
}

function getFieldValue(fieldId: string): any {
  const val = props.formData[`${props.section.id}.${fieldId}`] ??
              props.formData[fieldId] ?? '';
  // Handle wrapper objects with a 'text' property (e.g., ClarificationWithFile)
  if (val && typeof val === 'object' && !Array.isArray(val) && 'text' in val) {
    return val.text;
  }
  return val;
}

function setFieldValue(fieldId: string, value: any) {
  emit('update:field', { sectionId: props.section.id, fieldId, value });
}

function forwardFieldAction(p: { action: string; fieldId: string }) {
  emit('field-action', { sectionId: props.section.id, action: p.action, fieldId: p.fieldId });
}

// Field visibility (String coercion to handle boolean checkbox values vs string showWhen config)
function evalShowWhenCondition(cond: { field?: string; values?: string[]; value?: string; notEmpty?: boolean; falsy?: boolean }): boolean {
  if (!cond.field) return true;
  const raw = getFieldValue(cond.field);
  const val = raw !== '' ? raw : props.formData[cond.field];
  const strVal = String(val ?? '');
  // falsy: 체크박스가 꺼져 있을 때만 보이게 한다.
  if (cond.falsy) return !val || strVal === 'false' || strVal === '0';
  // notEmpty: 값이 아직 선택 안 된 이전 필드에 종속된 단계적 노출(cascade)에 사용 — 특정 값 나열 대신
  // "이전 필드가 뭐든 선택되긴 했는지"만 본다(CRS Name Search처럼 선택할 때마다 다음 필드가 나타나는 화면).
  if (cond.notEmpty) return strVal !== '';
  if (cond.values) return cond.values.includes(strVal) || cond.values.includes(val);
  if (cond.value) return strVal === String(cond.value);
  return true;
}

function isFieldVisible(field: FieldDef): boolean {
  if (!field.showWhen) return true;
  // all: 여러 조건 AND — 예: "category=LOCAL 이면서 companyType 선택됨"처럼 두 필드에 동시 종속될 때 사용.
  if (field.showWhen.all?.length) return field.showWhen.all.every(evalShowWhenCondition);
  return evalShowWhenCondition(field.showWhen);
}

// Address get/set
function getAddressValue(addressId: string): Record<string, any> {
  return props.formData[`${props.section.id}._addr.${addressId}`] || {};
}

function setAddressValue(addressId: string, value: Record<string, any>) {
  emit('update:address', { sectionId: props.section.id, addressId, value });
}

// Conditional group visibility
function isConditionalGroupVisible(cg: any): boolean {
  // showWhenFilled: 다른 필드에 값이 있을 때만 노출(비서를 고르면 나머지 칸이 열린다)
  if (cg?.showWhenFilled) {
    const v = getFieldValue(cg.showWhenFilled);
    return !!(v || v === 0);
  }
  if (!cg?.showWhen) return true;
  const val = getFieldValue(cg.showWhen.field);
  return String(val ?? '') === String(cg.showWhen.value);
}

// Handle conditional field updates (e.g., employee total calculation)
function handleConditionalFieldUpdate(field: FieldDef, value: any) {
  setFieldValue(field.id, value);

  if (field.handler === 'updateTotalEmployees') {
    const ft = Number(getFieldValue('fullTimeLocal') || 0);
    const ff = Number(getFieldValue('fullTimeForeign') || 0);
    const pt = Number(getFieldValue('partTimeLocal') || 0);
    const pf = Number(getFieldValue('partTimeForeign') || 0);
    let total = ft + ff + pt + pf;
    if (field.id === 'fullTimeLocal') total = Number(value || 0) + ff + pt + pf;
    else if (field.id === 'fullTimeForeign') total = ft + Number(value || 0) + pt + pf;
    else if (field.id === 'partTimeLocal') total = ft + ff + Number(value || 0) + pf;
    else if (field.id === 'partTimeForeign') total = ft + ff + pt + Number(value || 0);
    setFieldValue('totalEmployees', total);
  }
}

// Branch addresses
const branchAddresses = ref<Record<string, any>[]>([]);

function addBranchAddress() {
  branchAddresses.value.push({ country: 'MALAYSIA' });
}

function removeBranchAddress() {
  branchAddresses.value.pop();
}

function updateBranchAddress(idx: number, value: Record<string, any>) {
  branchAddresses.value[idx] = value;
}

// Attachment list (Supporting Documents) — CRS 의 "Add Attachment" 표와 같은 구조
/**
 * 첨부 행. `file` 은 이번 세션에 고른 파일이고,
 * `documentId`/`fileName` 은 **임시저장 때 CRS 에 이미 올려 둔 첨부**다.
 * File 은 JSON 으로 저장되지 않으므로, 재진입 시 되살아나는 건 이 두 값이다.
 */
const attachmentRows = ref<{
  description: string;
  file: File | null;
  documentId?: string;
  fileName?: string;
}[]>([]);

function emitAttachments() {
  emit('update:attachments', { sectionId: props.section.id, items: attachmentRows.value.map(r => ({ ...r })) });
}
function addAttachment() {
  attachmentRows.value.push({ description: '', file: null, documentId: '', fileName: '' });
  emitAttachments();
}
function removeAttachment(idx: number) {
  attachmentRows.value.splice(idx, 1);
  emitAttachments();
}
function updateAttachment(idx: number, key: 'description' | 'file', value: any) {
  const row = attachmentRows.value[idx];
  if (!row) return;
  if (key === 'file') {
    // v-file-input 은 배열로 줄 수 있다 — 한 행에 한 파일만 쓴다.
    const f = Array.isArray(value) ? (value[0] || null) : value;
    row.file = f;
    // 파일을 바꾸면 전에 올려 둔 CRS 첨부는 더 이상 이 행의 것이 아니다.
    row.documentId = '';
    row.fileName = f ? f.name : '';
  } else {
    row.description = value;
  }
  emitAttachments();
}
/**
 * 저장분 복원. File 객체는 JSON 으로 남길 수 없으므로,
 * 임시저장 때 CRS 에 올려 둔 `documentId`/`fileName` 으로 "첨부됨" 상태를 되살린다.
 */
function setAttachments(items: any[]) {
  attachmentRows.value = (items || []).map((r: any) => ({
    description: r?.description || '',
    file: r?.file || null,
    documentId: r?.documentId || '',
    fileName: r?.fileName || (r?.file ? r.file.name : ''),
  }));
}

// Simple list (business codes)
const listItems = ref<any[]>(['']);

function addListItem() {
  listItems.value.push('');
}

function removeListItem(idx: number) {
  listItems.value.splice(idx, 1);
  emit('update:list', { sectionId: props.section.id, items: [...listItems.value] });
}

function updateListItem(idx: number, value: any) {
  listItems.value[idx] = value;
  emit('update:list', { sectionId: props.section.id, items: [...listItems.value] });
}

// Dynamic list (directors, shareholders, promoters)
const dynamicItems = ref<Record<string, any>[]>([]);

function addDynamicItem() {
  const defaultType = props.section.itemTemplate?.typeSelector
    ? (props.section.itemTemplate.typeSelector.options?.[0]?.value || 'Individual')
    : undefined;
  dynamicItems.value.push(defaultType ? { type: defaultType } : {});
  emit('update:dynamicList', { sectionId: props.section.id, items: [...dynamicItems.value] });
}

function removeDynamicItem(idx: number) {
  dynamicItems.value.splice(idx, 1);
  emit('update:dynamicList', { sectionId: props.section.id, items: [...dynamicItems.value] });
}

function updateDynamicItem(idx: number, data: Record<string, any>) {
  dynamicItems.value[idx] = data;
  emit('update:dynamicList', { sectionId: props.section.id, items: [...dynamicItems.value] });
}

function handleMemberToggle(idx: number, isAlsoMember: boolean) {
  emit('member-toggle', { index: idx, isAlsoMember, directorData: { ...dynamicItems.value[idx] } });
}

// Date picker change for standard section fields
const MONTH_NAMES = ['JANUARY', 'FEBRUARY', 'MARCH', 'APRIL', 'MAY', 'JUNE', 'JULY', 'AUGUST', 'SEPTEMBER', 'OCTOBER', 'NOVEMBER', 'DECEMBER'];

function formatDateToDisplay(date: Date): string {
  return `${date.getDate()} ${MONTH_NAMES[date.getMonth()]} ${date.getFullYear()}`;
}

function handleStandardDatePick(fieldId: string, val: any) {
  if (!val) return;
  const date = new Date(val);
  if (!isNaN(date.getTime())) {
    // Use user's dateFormat preference (formatDateOnly reads from sessionStore)
    const userFormatted = formatDateOnly(date);
    setFieldValue(fieldId, userFormatted || formatDateToDisplay(date));
  }
}

function handleStandardDateBlur(fieldId: string) {
  const val = getFieldValue(fieldId);
  if (!val) return;
  // Try to reformat using user's dateFormat
  const userFormatted = formatDateOnly(val);
  if (userFormatted && userFormatted !== val) {
    setFieldValue(fieldId, userFormatted);
  }
}

// 헤더 dateFormat 변경 시 이 섹션의 date 필드 즉시 재포맷
const sessionStore = useSessionStore();
const dateFormat = computed(() => sessionStore.user?.user?.dateFormat || '');
watch(dateFormat, () => {
  const fields = props.section.fields || [];
  for (const field of fields) {
    if (field.type !== 'date') continue;
    const val = getFieldValue(field.id);
    if (!val) continue;
    const formatted = formatDateOnly(String(val));
    if (formatted && formatted !== val) setFieldValue(field.id, formatted);
  }
});

/**
 * Total Employees 는 CRS 와 마찬가지로 입력칸이 아니라 4개 항목의 합이다.
 *
 * 그런데 합산은 '사용자가 칸에 입력했을 때'(handleConditionalFieldUpdate)만 돌아서,
 * 저장본을 다시 불러오거나 백엔드/T-Link 프리필로 4개 값이 채워지면 Total 만
 * 옛 값으로 남았다(실측: 4/1/2/1 인데 Total 6). 화면에 틀린 합이 보이므로 여기서 맞춘다.
 * CRS 로 보내는 payload 는 별도로 다시 합산하니 제출값은 영향 없다.
 */
const employeeCountFieldIds = ['fullTimeLocal', 'fullTimeForeign', 'partTimeLocal', 'partTimeForeign'];
const hasEmployeeTotalField = computed(() => {
  const groups = (props.section as any).conditionalGroups || [];
  return groups.some((g: any) => (g.fields || []).some((f: any) => f.id === 'totalEmployees'));
});
watch(
  () => (hasEmployeeTotalField.value
    ? employeeCountFieldIds.map((id) => getFieldValue(id)).join('|')
    : ''),
  () => {
    if (!hasEmployeeTotalField.value) return;
    const total = employeeCountFieldIds.reduce((sum, id) => sum + (Number(getFieldValue(id)) || 0), 0);
    if (Number(getFieldValue('totalEmployees') || 0) !== total) setFieldValue('totalEmployees', total);
  },
  { immediate: true },
);

// File changes from fields
function handleFileChange(payload: { fieldId: string; file: File | null }) {
  emit('update:field', {
    sectionId: props.section.id,
    fieldId: `_file.${payload.fieldId}`,
    value: payload.file
  });
}

// Expose for parent
function getListItems() {
  return [...listItems.value];
}

function getDynamicItems() {
  return dynamicItems.value.map(item => ({ ...item }));
}

function getBranchAddresses() {
  return branchAddresses.value.map(addr => ({ ...addr }));
}

function setListItems(items: any[]) {
  listItems.value = [...items];
}

function setDynamicItems(items: Record<string, any>[]) {
  dynamicItems.value = items.map(i => ({ ...i }));
}

function setBranchAddresses(addrs: Record<string, any>[]) {
  branchAddresses.value = addrs.map(a => ({ ...a }));
}

defineExpose({
  setAttachments,
  getListItems,
  getDynamicItems,
  getBranchAddresses,
  setListItems,
  setDynamicItems,
  setBranchAddresses,
});
</script>

<style scoped>
.form-section {
  padding: 4px 0;
}

.field-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px 12px;
}

@media (max-width: 960px) {
  .field-grid {
    grid-template-columns: 1fr;
  }
}
</style>
